import React, { useState, useEffect } from "react";
import { Link } from "../../components/Link";
import { Button } from "../../components/Button";
import "./UsersPagination.css";
import { user, auth } from "../../store";
import searchIcon from "../../icons/search.svg"
import leftArrowButton from "../../icons/chevron-left-large.svg"
import rightArrowButton from "../../icons/chevron-right-large.svg"

const TYPE_TABS = [
  { key: "", label: "전체" },
  { key: "admin", label: "관리자" },
  { key: "staff", label: "스태프" },
  { key: "user", label: "일반" },
];

export const UsersPagination = () => {
  const users = user((state) => state.users || []);
  const getUsers = user((state) => state.getUsers);
  const searchUsers = user((state) => state.searchUsers);
  const updateUserType = user((state) => state.updateUserType);
  const deleteUsers = user((state) => state.deleteUsers);
  const isLoading = user((state) => state.isLoading);
  // 계정 유형 변경은 admin 계정만 가능. staff/user에게는 버튼 자체를 노출하지 않는다.
  const myUserType = auth((state) => state.user?.userType);
  const isAdmin = myUserType === "admin";
  const [currentPage, setCurrentPage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [activeType, setActiveType] = useState(""); // "" | admin | staff | user
  const [selectedIds, setSelectedIds] = useState([]);
  const postsPerPage = 7; // 한 페이지당 보여줄 데이터 수를 7로 변경
  const totalPosts = users.length;

  const refetch = () => {
    if (searchKeyword) {
      searchUsers(searchKeyword, activeType);
    } else {
      getUsers(activeType);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds([]); // 목록이 바뀌면 선택 초기화
    if (searchKeyword) {
      searchUsers(searchKeyword, activeType);
    } else {
      getUsers(activeType);
    }
  }, [searchKeyword, activeType]);

  // admin 계정은 삭제 대상에서 제외(자기 자신/마지막 admin 삭제로 인한 lockout 차단)
  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (!selectedIds.length) return;
    const ok = window.confirm(
      `선택한 ${selectedIds.length}개 계정을 삭제합니다.\n` +
        "삭제된 계정과 관련 데이터는 복구할 수 없습니다. 진행하시겠습니까?"
    );
    if (!ok) return;
    const { success, failed } = await deleteUsers(selectedIds);
    setSelectedIds([]);
    refetch();
    if (failed > 0) {
      alert(`${success}개 삭제 완료, ${failed}개 실패했습니다.`);
    } else {
      alert(`${success}개 계정을 삭제했습니다.`);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // 강등(staff→user) / 승격(user→staff) 토글. admin 행은 호출 자체가 없음.
  const handleChangeType = async (targetUser) => {
    if (targetUser.userType === "staff") {
      const ok = window.confirm(
        `${targetUser.name || "이 스태프"} 님을 일반 유저로 강등합니다.\n` +
          "이 스태프의 관리 권한이 즉시 회수됩니다. (계정·이력은 그대로 유지)"
      );
      if (!ok) return;
      await updateUserType(targetUser.id, "user");
    } else if (targetUser.userType === "user") {
      const ok = window.confirm(
        `${targetUser.name || "이 회원"} 님을 스태프로 지정합니다.\n` +
          "스태프 관리 기능(고객 후기·칼럼 관리)에 접근할 수 있게 됩니다."
      );
      if (!ok) return;
      await updateUserType(targetUser.id, "staff");
    }
  };

  const renderPageButtons = () => {
    const pageButtons = [];
    const totalPages = Math.ceil(totalPosts / postsPerPage);
    let startPage = Math.max(currentPage - 2, 1);
    let endPage = Math.min(startPage + 4, totalPages);

    // 현재 페이지가 첫 번째 페이지에 가까워서 페이지 버튼이 충분하지 않은 경우
    if (endPage - startPage < 4) {
      startPage = Math.max(endPage - 4, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <button
          key={i}
          className={
            currentPage === i
              ? "users-pagination-page-button active"
              : "users-pagination-page-button"
          }
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    return pageButtons;
  };

  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const userlist = users?.slice(indexOfFirstPost, indexOfLastPost);

  // 현재 페이지에서 삭제 가능한(admin 제외) 행 기준 전체선택 토글
  const deletableOnPage = (userlist || [])
    .filter((u) => u.userType !== "admin")
    .map((u) => u.id);
  const allSelectedOnPage =
    deletableOnPage.length > 0 &&
    deletableOnPage.every((id) => selectedIds.includes(id));

  const toggleSelectAllOnPage = () => {
    if (allSelectedOnPage) {
      setSelectedIds((prev) =>
        prev.filter((id) => !deletableOnPage.includes(id))
      );
    } else {
      setSelectedIds((prev) =>
        Array.from(new Set([...prev, ...deletableOnPage]))
      );
    }
  };

  return (
    <div className="admin-users-main">
      <div className="users-pagination-container">
        <div className="users-pagination-sort-wrap">
          <div className="users-type-tabs">
            {TYPE_TABS.map((tab) => (
              <button
                key={tab.key || "all"}
                type="button"
                className={`users-type-tab ${
                  activeType === tab.key ? "active" : ""
                }`}
                onClick={() => setActiveType(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="users-pagination-search-input-wrap">
            <img src={searchIcon} className="search-icon" alt="검색 이미지" />
            <input
              type="search"
              className="users-pagination-search-input"
              placeholder="Search..."
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
            />
          </div>
          {isAdmin && (
            <Button
              label={
                selectedIds.length
                  ? `선택 삭제 (${selectedIds.length})`
                  : "선택 삭제"
              }
              variant="danger"
              onClick={handleDeleteSelected}
              className={selectedIds.length ? "" : "disabled"}
              style={{
                marginLeft: "auto",
                height: "35px",
                fontSize: "14px",
                padding: "0 16px",
                ...(selectedIds.length
                  ? {}
                  : { opacity: 0.5, pointerEvents: "none" }),
              }}
            />
          )}
        </div>
        <div className="users-list">
          <div className="users-list-header">
            <div>
              {isAdmin && (
                <input
                  type="checkbox"
                  aria-label="현재 페이지 전체 선택"
                  checked={allSelectedOnPage}
                  onChange={toggleSelectAllOnPage}
                  disabled={deletableOnPage.length === 0}
                />
              )}
            </div>
            <div>이름</div>
            <div>이메일</div>
            <div>연락처</div>
            <div>유저종류</div>
            <div>가입일자</div>
            <div>관리</div>
          </div>
          {isLoading ? (
            <div className="users-item">
              <div></div>
              <p className="">Loading...</p>
            </div>
          ) : (
            userlist.map((user) => (
              <div key={user.id} className="users-item">
                <div>
                  <input
                    type="checkbox"
                    name={user.id}
                    id={user.id}
                    checked={selectedIds.includes(user.id)}
                    onChange={() => toggleSelect(user.id)}
                    disabled={!isAdmin || user.userType === "admin"}
                  />
                </div>
                <Link
                  to={`/admin/users/${user.id}`}
                  className="post-item-link"
                  style={{
                    backgroundColor: "transparent",
                    fontFamily: '"Lexend", Helvetica',
                    fontWeight: "700",
                    fontSize: "14px",
                    color: "#dee1e6",
                    width: "auto",
                    height: "18px",
                    textAlign: "left",
                    display: "block",
                  }}
                >
                  <div>{user.name}</div>
                </Link>
                <div>{user.email}</div>

                <div>{user.phone}</div>
                <div>{user.userType}</div>
                <div>
                  {new Date(user.createdAt).toLocaleDateString("ko-KR")}
                </div>
                <div className="users-item-manage">
                  {isAdmin && user.userType === "staff" && (
                    <Button
                      label="강등"
                      variant="danger"
                      onClick={() => handleChangeType(user)}
                      style={{ height: "30px", fontSize: "13px", padding: "0 12px" }}
                    />
                  )}
                  {isAdmin && user.userType === "user" && (
                    <Button
                      label="스태프 지정"
                      onClick={() => handleChangeType(user)}
                      style={{ height: "30px", fontSize: "13px", padding: "0 12px" }}
                    />
                  )}
                  {(!isAdmin || user.userType === "admin") && (
                    <span className="users-item-manage-empty">-</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        <div className="users-pagination-footer">
          <div className="users-pagination-count">
            {users ? users.length : 1} results
          </div>
          <div className="users-pagination-buttons">
            <button
              className={`users-pagination-button ${
                currentPage === 1 ? "disabled" : ""
              }`}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <img
                className="img-11"
                alt="Chevron left large"
                src={leftArrowButton}
              />
            </button>
            <div className="users-pagination-button-wrap">
              {renderPageButtons()}
            </div>
            <button
              className={`users-pagination-button ${
                currentPage === Math.ceil(totalPosts / postsPerPage)
                  ? "disabled"
                  : ""
              }`}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === Math.ceil(totalPosts / postsPerPage)}
            >
              <img
                className="img-11"
                alt="Chevron right large"
                src={rightArrowButton}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
